'use client'

import { useQuery } from '@apollo/client/react'
import { type FC, useCallback, useState } from 'react'

import {
  Button,
  LoadingSpinner,
  TypeStyleBodyDefault,
  TypeStyleHeadlineExtraExtraSmall,
} from '@syconium/little-miss-figgy'

import { gql } from '../../../../../../../__generated__/graphql/catalog'
import { useFeatureFlag } from '../../../../../../_providers/ExperimentationProvider.client'
import { useLocalization } from '../../../../../../_providers/LocalizationProvider.client'
import { useTranslation } from '../../../../../../_providers/TranslationProvider.client'
import {
  PillTab,
  PillTabList,
  PillTabPanel,
  PillTabProvider,
} from '../../../../../tabs/PillTab/PillTab.client'

import { dedupeMeasureTips } from './sizeChartComponents'
import {
  formatSizeChart,
  formatSizeChartColumns,
  hasRenderableContent,
  isFitGuideTable,
} from './sizeChartData'
import { SizeChartFitGuideSection } from './SizeChartFitGuideSection'
import * as Styles from './SizeChartModalContent.styles'
import { SizeChartSizingSection } from './SizeChartSizingSection'

const SIZING_TAB_ID = 'size-chart-sizing'
const STYLE_AND_FIT_TAB_ID = 'size-chart-style-and-fit'

const ACUITY_SCHEDULER_URL =
  'https://app.acuityscheduling.com/schedule.php?owner=40507333&ref=booking_button'

export const FETCH_SIZE_CHART = gql(`
    query FetchSizeChart($handle: String!) {
      productGroupSizeChart(handle: $handle) {
        ...SizeChart
      }
    }
  `)

export type SizeChartModalContentProps = {
  productHandle: string
}

export const SizeChartModalContent: FC<SizeChartModalContentProps> = ({ productHandle }) => {
  const {
    magnolia: { general },
    sizesSection: { sizeChart: strings },
  } = useTranslation()
  const { isUSRegion } = useLocalization()
  const isAcuitySchedulerEnabled =
    useFeatureFlag<'true' | 'false'>({
      key: 'acuity-scheduler',
      controlVariant: 'false',
      ifAccessedPriorToDecisionInitialization: 'lock-pageview-to-controlVariant',
    }) === 'true'

  const { data, loading } = useQuery(FETCH_SIZE_CHART, {
    skip: !productHandle,
    variables: { handle: productHandle },
  })

  const sizeChart = data?.productGroupSizeChart

  const [showMetric, setShowMetric] = useState<boolean>(!isUSRegion)
  const onToggleUnit = useCallback(() => setShowMetric(previous => !previous), [])

  if (loading) {
    return (
      <Styles.SizeChartLoadingState>
        <LoadingSpinner label={general.loading} size='64px' />
      </Styles.SizeChartLoadingState>
    )
  }

  if (!sizeChart) return null

  // CX books fit consultations through Acuity, which only serves the US for now.
  const HelpSection =
    isAcuitySchedulerEnabled && isUSRegion ? (
      <>
        <Styles.Divider />

        <Styles.HelpBox>
          <TypeStyleHeadlineExtraExtraSmall asTag='h2'>
            {strings.needHelpHeadline}
          </TypeStyleHeadlineExtraExtraSmall>
          <TypeStyleBodyDefault asTag='p'>{strings.needHelpBody}</TypeStyleBodyDefault>
          <Styles.HelpBoxCtas>
            {/* TODO: open the chat widget once Siena/Zendesk expose a programmatic entry point. */}
            {/* <Button type='button' variant='primary' size='large'>
              {strings.chatWithUs}
            </Button> */}
            <Button
              as='a'
              href={ACUITY_SCHEDULER_URL}
              rel='noopener noreferrer'
              size='large'
              target='_blank'
              variant='secondary'
            >
              {strings.bookFitConsult}
            </Button>
          </Styles.HelpBoxCtas>
        </Styles.HelpBox>
      </>
    ) : null

  // Charts can be returned from contentful as columnNames/columnValues OR tables
  if (!sizeChart.tables?.length) {
    const chart = formatSizeChartColumns(sizeChart.columnNames, sizeChart.columnValues)
    if (chart.sizes.length === 0) return null

    return (
      <Styles.Content>
        <SizeChartSizingSection
          measureTips={sizeChart.tips}
          onToggleUnit={onToggleUnit}
          productHandle={productHandle}
          showMetric={showMetric}
          tables={[{ chart, id: sizeChart.id, subtitle: null }]}
          title={sizeChart.title}
        />
        {HelpSection}
      </Styles.Content>
    )
  }

  const tables = sizeChart.tables.filter(table => hasRenderableContent(table, showMetric))

  // Only a table carrying a visual filters section earns a tab of its own; everything else
  // stacks up inside the sizing tab, one grid per subtitle.
  const fitGuideTables = tables.filter(isFitGuideTable)
  const sizingTables = tables.filter(table => !isFitGuideTable(table))

  const sizingSection =
    sizingTables.length > 0 ? (
      <SizeChartSizingSection
        measureTips={dedupeMeasureTips(sizingTables)}
        onToggleUnit={onToggleUnit}
        productHandle={productHandle}
        showMetric={showMetric}
        tables={sizingTables.map(table => ({
          chart: formatSizeChart(table.cells, showMetric),
          id: table.id,
          subtitle: table.subtitle,
        }))}
        title={sizingTables[0]?.title}
      />
    ) : null

  const fitGuideSection = fitGuideTables.map(table => (
    <SizeChartFitGuideSection key={table.id} table={table} />
  ))

  if (tables.length === 0) return null

  // With only one kind of content there is nothing to tab between.
  if (fitGuideTables.length === 0 || sizingTables.length === 0) {
    return (
      <Styles.Content>
        {sizingSection ?? fitGuideSection}
        {HelpSection}
      </Styles.Content>
    )
  }

  return (
    <Styles.Content>
      <PillTabProvider defaultSelectedId={SIZING_TAB_ID} defaultActiveId={SIZING_TAB_ID}>
        <PillTabList animated>
          <PillTab id={SIZING_TAB_ID}>{strings.tabs.sizing}</PillTab>
          <PillTab id={STYLE_AND_FIT_TAB_ID}>{strings.tabs.stylesAndFit}</PillTab>
        </PillTabList>

        <PillTabPanel tabId={SIZING_TAB_ID} render={<Styles.TabPanel />}>
          {sizingSection}
          {HelpSection}
        </PillTabPanel>

        <PillTabPanel tabId={STYLE_AND_FIT_TAB_ID} render={<Styles.TabPanel />}>
          {fitGuideSection}
          {HelpSection}
        </PillTabPanel>
      </PillTabProvider>
    </Styles.Content>
  )
}
